import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdvancedPDFViewer = ({
  pdfUrl,
  logoUrl,
  companyName = 'DISGENET',
  title = 'Brochure',
  showDownload = true,
}: {
  pdfUrl: string;
  logoUrl?: string;
  companyName?: string;
  title?: string;
  showDownload?: boolean;
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleLoad = () => {
    setIsLoading(false);
    setError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setError(true);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = title || 'disgenet-brochure.pdf';
    link.click();
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#f8f9fa',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Header */}
      {!fullscreen && (
        <div
          style={{
            backgroundColor: 'white',
            padding: '12px 20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '64px',
            zIndex: 10000,
            borderBottom: '1px solid #e9ecef',
          }}
        >
          <div
            onClick={handleLogoClick}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={companyName}
                style={{
                  height: '40px',
                  maxWidth: '200px',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#212529',
                }}
              >
                {companyName}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {showDownload && (
              <button
                onClick={handleDownload}
                style={{
                  background: 'none',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#495057',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = '#f8f9fa';
                  target.style.borderColor = '#adb5bd';
                }}
                onMouseOut={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = 'transparent';
                  target.style.borderColor = '#dee2e6';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
                Download
              </button>
            )}

            <button
              onClick={toggleFullscreen}
              style={{
                background: 'none',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                padding: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#495057',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                const target = e.target as HTMLElement;
                target.style.backgroundColor = '#f8f9fa';
                target.style.borderColor = '#adb5bd';
              }}
              onMouseOut={(e) => {
                const target = e.target as HTMLElement;
                target.style.backgroundColor = 'transparent';
                target.style.borderColor = '#dee2e6';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M5,5H10V7H7V10H5V5M14,5H19V10H17V7H14V5M17,14H19V19H14V17H17V14M10,17V19H5V14H7V17H10Z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Container */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#e9ecef',
        }}
      >
        {/* Loading Spinner */}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '12px',
              }}
            ></div>
            <p style={{ color: '#6c757d', fontSize: '14px', margin: 0 }}>Loading document...</p>
          </div>
        )}

        {/* PDF Iframe */}
        {!error && (
          <iframe
            src={pdfUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
            }}
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
          />
        )}

        {/* Error State */}
        {error && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              padding: '40px 20px',
              maxWidth: '500px',
            }}
          >
            <div
              style={{
                fontSize: '48px',
                marginBottom: '16px',
              }}
            >
              📄
            </div>
            <h3
              style={{
                color: '#495057',
                fontSize: '20px',
                marginBottom: '12px',
              }}
            >
              Could not show document.
            </h3>
            <p
              style={{
                color: '#6c757d',
                fontSize: '16px',
                marginBottom: '24px',
                lineHeight: 1.5,
              }}
            >
              Your navigator could not show this PDF. You can download it to see it in your device.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'white',
                  backgroundColor: '#007bff',
                  textDecoration: 'none',
                  fontSize: '16px',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseOver={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = '#0056b3';
                }}
                onMouseOut={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = '#007bff';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
                Download PDF
              </a>
              <button
                onClick={handleLogoClick}
                style={{
                  color: '#495057',
                  backgroundColor: 'transparent',
                  border: '1px solid #dee2e6',
                  fontSize: '16px',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseOut={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.backgroundColor = 'transparent';
                }}
              >
                Back to home
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .pdf-header {
            padding: 8px 12px !important;
            flex-direction: column;
            align-items: flex-start !important;
            gap: 8px;
            min-height: auto !important;
          }
          
          .pdf-header-title {
            font-size: 14px !important;
            margin-left: 0 !important;
            padding-left: 0 !important;
            border-left: none !important;
          }
          
          .pdf-header-actions {
            gap: 4px !important;
          }
          
          .pdf-header-actions button {
            padding: 6px 8px !important;
            font-size: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdvancedPDFViewer;
